import React, { useCallback, useEffect, useState } from "react";
import instance, { fetchAllDepartments } from "../../api/api";

const INPUT =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-caribbeanCurrent focus:outline-none focus:ring-1 focus:ring-caribbeanCurrent";

const isSameName = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();

function DepartmentRow({ department, departments, onSaved, onDeleted }) {
  const [mode, setMode] = useState("view"); // view | edit | confirmDelete
  const [name, setName] = useState(department.name);
  const [description, setDescription] = useState(department.description || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const trimmedName = name.trim();
  const duplicate = departments.some((d) => d.id !== department.id && isSameName(d.name, trimmedName));
  const unchanged = trimmedName === department.name && description.trim() === (department.description || "");
  const problem = !trimmedName
    ? "Name can't be empty."
    : duplicate
    ? `"${trimmedName}" already exists.`
    : !description.trim()
    ? "Add a short description."
    : null;

  const startEdit = () => {
    setName(department.name);
    setDescription(department.description || "");
    setError(null);
    setMode("edit");
  };

  const cancel = () => {
    setError(null);
    setMode("view");
  };

  const save = async (e) => {
    e.preventDefault();
    if (problem || unchanged || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await instance.put(`/api/departments/update/${department.id}`, {
        name: trimmedName,
        description: description.trim(),
      });
      onSaved(res.data.data, department.name);
      setMode("view");
    } catch (err) {
      setError(err?.response?.data?.message ?? "Could not save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await instance.delete(`/api/departments/delete/${department.id}`);
      onDeleted(department);
    } catch (err) {
      setError(err?.response?.data?.message ?? "Could not delete. Please try again.");
      setBusy(false);
    }
  };

  if (mode === "edit") {
    return (
      <li className="py-3">
        <form onSubmit={save} className="grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Department name"
            maxLength={100}
            autoFocus
            className={INPUT}
          />
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            aria-label="Department description"
            maxLength={300}
            className={INPUT}
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={Boolean(problem) || unchanged || busy}
              className="rounded-lg bg-caribbeanCurrent px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40"
            >
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={cancel}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
        {(problem || error) && (
          <p className="mt-1.5 text-sm text-red-600" aria-live="polite">{problem || error}</p>
        )}
        {!problem && !error && !isSameName(trimmedName, department.name) && (
          <p className="mt-1.5 text-xs text-gray-500">
            Users and risks in this department keep it; they'll show the new name. Existing risk numbers don't change.
          </p>
        )}
      </li>
    );
  }

  return (
    <li className="py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{department.name}</p>
          {department.description && <p className="text-xs text-gray-500">{department.description}</p>}
        </div>
        {mode === "view" ? (
          <div className="flex shrink-0 gap-1">
            <button
              onClick={startEdit}
              className="rounded-md px-2.5 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100"
            >
              Edit
            </button>
            <button
              onClick={() => { setError(null); setMode("confirmDelete"); }}
              className="rounded-md px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        ) : (
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-xs text-gray-600">Delete "{department.name}"?</span>
            <button
              onClick={remove}
              disabled={busy}
              className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {busy ? "Deleting…" : "Delete"}
            </button>
            <button
              onClick={cancel}
              disabled={busy}
              className="rounded-md px-2.5 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      {error && <p className="mt-1.5 text-sm text-red-600" aria-live="polite">{error}</p>}
    </li>
  );
}

export default function DepartmentSettings() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const result = await fetchAllDepartments();
    if (result === undefined) setLoadError("Could not load departments.");
    setDepartments(result || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const trimmedName = name.trim();
  const duplicate = departments.some((d) => isSameName(d.name, trimmedName));
  const problem = !trimmedName
    ? null
    : duplicate
    ? `"${trimmedName}" already exists.`
    : !description.trim()
    ? "Add a short description."
    : null;
  const canSave = trimmedName && description.trim() && !duplicate && !saving;

  const addDepartment = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await instance.post("/api/departments/create", {
        name: trimmedName,
        description: description.trim(),
      });
      setDepartments((list) => [...list, res.data.data]);
      setNotice(`"${trimmedName}" added. You can now assign users to it.`);
      setName("");
      setDescription("");
    } catch (err) {
      setError(err?.response?.data?.message ?? "Could not add the department. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Existing departments */}
      <div className="px-5 py-4">
        {loading ? (
          <div className="space-y-2">
            {[0, 1].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-gray-100" />)}
          </div>
        ) : loadError ? (
          <p className="text-sm text-red-600">
            {loadError}{" "}
            <button onClick={load} className="font-semibold underline">Try again</button>
          </p>
        ) : departments.length === 0 ? (
          <p className="text-sm text-gray-500">No departments yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {departments.map((d) => (
              <DepartmentRow
                key={d.id}
                department={d}
                departments={departments}
                onSaved={(updated, oldName) => {
                  setDepartments((list) => list.map((x) => (x.id === updated.id ? updated : x)));
                  setError(null);
                  setNotice(oldName !== updated.name ? `Renamed "${oldName}" to "${updated.name}".` : `"${updated.name}" saved.`);
                }}
                onDeleted={(removed) => {
                  setDepartments((list) => list.filter((x) => x.id !== removed.id));
                  setError(null);
                  setNotice(`"${removed.name}" deleted.`);
                }}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Add department */}
      <form onSubmit={addDepartment} className="border-t border-gray-100 bg-gray-50 px-5 py-4 rounded-b-xl">
        <p className="mb-3 text-[11px] font-bold uppercase tracking-wide text-gray-400">Add department</p>
        <div className="grid gap-3 sm:grid-cols-[1fr_2fr_auto]">
          <input
            value={name}
            onChange={(e) => { setName(e.target.value); setNotice(null); }}
            placeholder="Name (e.g. Student Affairs)"
            aria-label="Department name"
            maxLength={100}
            className={INPUT}
          />
          <input
            value={description}
            onChange={(e) => { setDescription(e.target.value); setNotice(null); }}
            placeholder="Short description"
            aria-label="Department description"
            maxLength={300}
            className={INPUT}
          />
          <button
            type="submit"
            disabled={!canSave}
            className="whitespace-nowrap rounded-lg bg-caribbeanCurrent px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40"
          >
            {saving ? "Adding…" : "Add department"}
          </button>
        </div>
        <div className="mt-2 min-h-[1.25rem] text-sm" aria-live="polite">
          {problem && <span className="text-red-600">{problem}</span>}
          {!problem && error && <span className="text-red-600">{error}</span>}
          {!problem && !error && notice && <span className="text-green-700">{notice}</span>}
        </div>
      </form>
    </div>
  );
}
